'use client';

import { IFormsTemplate, updateFormsTemplate } from '@commenda/api';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Textarea,
  useToggle,
} from '@commenda/kakashi';
import { FormsTemplateStatus } from '@commenda/types';
import { useEffect, useState } from 'react';
import { CreateKeyModal } from '../template-keys/CreateKeyModal';
import { EditableKeyCell } from './EditableKeyCell';
import { EditableStatusCell } from './EditableStatusCell';

interface EditTemplateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template: IFormsTemplate | null;
  onSuccess: () => void;
  onNavigateToEditor: (template: IFormsTemplate) => void;
}

export function EditTemplateModal({
  open,
  onOpenChange,
  template,
  onSuccess,
  onNavigateToEditor,
}: EditTemplateModalProps) {
  const isCreateKeyModalOpen = useToggle(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: FormsTemplateStatus.DRAFT,
    key: null as string | null,
    keyName: null as string | null,
    version: '',
    versionNote: '',
  });

  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name || '',
        description: template.description || '',
        status: template.status || FormsTemplateStatus.DRAFT,
        key: template.key || null,
        keyName: template.keyName || null,
        version: template.version || '',
        versionNote: template.versionNote || '',
      });
    }
  }, [template]);

  const handleSave = async () => {
    if (!template) return;

    setIsSaving(true);
    try {
      const result = await updateFormsTemplate(template.id, {
        name: formData.name,
        description: formData.description || null,
        status: formData.status,
        key: formData.key,
        version: formData.version,
        versionNote: formData.versionNote || null,
      });

      if (result.success) {
        onSuccess();
        onOpenChange(false);
      }
    } finally {
      setIsSaving(false);
    }
  };

  if (!template) return null;

  return (
    <>
      <CreateKeyModal
        open={isCreateKeyModalOpen.value}
        onOpenChange={isCreateKeyModalOpen.toggle}
        onSuccess={(createdKey) => {
          setFormData((prev) => ({
            ...prev,
            key: createdKey.key,
            keyName: createdKey.name,
          }));
        }}
      />
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader className="flex flex-col gap-xs justify-start items-start">
            <DialogTitle>Edit Template</DialogTitle>
            <DialogDescription>
              Update the template details or{' '}
              <button
                type="button"
                className="text-fg-brand underline hover:text-fg-brand-hover"
                onClick={() => {
                  onOpenChange(false);
                  onNavigateToEditor(template);
                }}
              >
                open in editor
              </button>{' '}
              to modify the form structure.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 px-4">
            <div className="space-y-2">
              <Label htmlFor="templateName">Template Name *</Label>
              <Input
                id="templateName"
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    name: e.target.value,
                  }))
                }
                placeholder="Enter template name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="templateDescription">Description</Label>
              <Textarea
                id="templateDescription"
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                placeholder="Enter template description"
                rows={3}
                className="min-h-20"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="templateStatus">Status *</Label>
                <EditableStatusCell
                  id={template.id}
                  status={formData.status}
                  onUpdate={(_id: string, newStatus: FormsTemplateStatus) => {
                    setFormData((prev) => ({
                      ...prev,
                      status: newStatus,
                    }));
                  }}
                  size="md"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="templateKey">Template Key</Label>
                <EditableKeyCell
                  template={{
                    key: formData.key ?? '',
                    keyName: formData.keyName ?? '',
                    id: template.id,
                  }}
                  onUpdate={async (
                    _id: string,
                    newKey: string | null,
                    newKeyName: string | null
                  ) => {
                    setFormData((prev) => ({
                      ...prev,
                      key: newKey,
                      keyName: newKeyName,
                    }));
                  }}
                  onAddKey={() => {
                    isCreateKeyModalOpen.on();
                  }}
                  size="md"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="templateVersion">Version *</Label>
              <Input
                id="templateVersion"
                value={formData.version}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    version: e.target.value,
                  }))
                }
                placeholder="1.0.0"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="templateVersionNote">Version Note</Label>
              <Textarea
                id="templateVersionNote"
                value={formData.versionNote}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    versionNote: e.target.value,
                  }))
                }
                placeholder="What changed in this version?"
                rows={2}
                className="min-h-20"
              />
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="secondary">Cancel</Button>
            </DialogClose>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={
                !formData.name ||
                !formData.status ||
                !formData.version ||
                isSaving
              }
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
